import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Home, Building2, Send, CheckCircle2, Plug, PlugZap, MessageCircle, TrendingDown } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useCurrency } from "../../lib/currency";
import { useAccount } from "../../lib/account";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ASSETS, PRODUCTS, formatQuote, financePlan, WHATSAPP_NUMBER, dieselAnnualUsd, upfrontDiscountRate } from "../../lib/data";

export const LeadForm = () => {
  const { t, lang } = useLang();
  const { cur, upfront, financeMonths } = useCurrency();
  const { user } = useAccount();
  const upfrontPct = Math.round(upfrontDiscountRate(cur.id) * 100);
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: "", use_case: "home", grid_type: "on_grid", budget: "unsure", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Prefill name/email for signed-in customers so the quote ties to their account.
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const recommended = PRODUCTS.find((p) => p.budget === form.budget) || PRODUCTS[0];
  const quoteUsd = upfront ? recommended.usd * (1 - upfrontDiscountRate(cur.id)) : recommended.usd;
  const isGhana = cur.id === "gh";
  const plan = isGhana ? financePlan(recommended.usd, financeMonths) : null;

  const openWhatsApp = () => {
    const gridLabel = form.grid_type === "off_grid" ? t.lead.offGrid : t.lead.onGrid;
    const lines = [
      "Hello Gesung, I'd like a quote.",
      form.name && `Name: ${form.name}`,
      form.location && `Location: ${form.location}`,
      `Use: ${form.use_case === "business" ? t.lead.business : t.lead.home}`,
      `Grid: ${gridLabel}`,
      `Model: Gesung ${recommended.model}`,
      `Estimated: ${cur.id !== "usd" ? t.catalog.approx + " " : ""}${formatQuote(quoteUsd, cur)}`,
      upfront && `Upfront payment (-${upfrontPct}%): yes`,
    ].filter(Boolean);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        model: recommended.model,
        currency: cur.currency,
        country: cur.id === "usd" ? "United States" : (lang === "fr" ? cur.labelFR : cur.labelEN),
        local_price: formatQuote(quoteUsd, cur),
        price_usd: Math.round(quoteUsd),
        upfront: !!upfront,
        annual_savings: formatQuote(dieselAnnualUsd(), cur),
        ...(plan ? {
          finance_months: plan.months,
          finance_monthly: formatQuote(plan.monthly, cur),
          finance_down: formatQuote(plan.down, cur),
          finance_rate: Math.round(plan.rate * 100),
          finance_total: formatQuote(plan.total, cur),
        } : {}),
      };
      await api.post("/leads", payload);
      setDone(true);
      toast.success(t.lead.success);
    } catch (err) {
      toast.error(t.lead.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative z-10 py-24 md:py-32 border-t border-white/10" data-testid="lead-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
        {/* Visual */}
        <Reveal className="hidden lg:block">
          <div className="relative rounded-2xl overflow-hidden h-full min-h-[560px] border border-white/10">
            <img src={ASSETS.family} alt="Powered family home" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 p-10">
              <p className="font-display text-4xl font-extrabold tracking-tighter">{t.footer.tagline}</p>
              <p className="text-white/60 mt-2 max-w-xs">{t.lead.sub}</p>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-white/10 bg-[#181818] p-7 md:p-10">
            <SectionKicker>{t.lead.kicker}</SectionKicker>
            <h2 className="font-display font-extrabold tracking-tighter text-3xl md:text-5xl leading-[0.95]">
              {t.lead.title}
            </h2>

            <div
              className="mt-6 rounded-xl border border-lime2/30 bg-gradient-to-r from-emerald2/15 to-lime2/[0.04] p-4 md:p-5"
              data-testid="lead-savings-callout"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-lime2/80">
                <TrendingDown size={14} /> {t.lead.savingsLabel}
              </div>
              <p className="mt-1 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-lime2 leading-none">
                {cur.id !== "usd" ? `${t.catalog.approx} ` : ""}
                <span data-testid="lead-savings-amount">{formatQuote(dieselAnnualUsd(), cur)}</span>{" "}
                <span className="text-white/70 text-sm md:text-base font-semibold align-middle">{t.lead.savingsSuf}</span>
              </p>
              <p className="mt-2 text-xs text-white/50">{t.lead.savingsNote}</p>
            </div>

            {done ? (
              <div className="mt-10 flex flex-col items-center text-center py-10" data-testid="lead-success">
                <CheckCircle2 size={56} className="text-lime2" />
                <p className="mt-5 font-display text-2xl font-bold">{t.lead.success}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="lead-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required value={form.name} onChange={set("name")} placeholder={t.lead.name} data-testid="lead-name" className="bg-charcoal border-white/15 h-12" />
                  <Input required value={form.phone} onChange={set("phone")} placeholder={t.lead.phone} data-testid="lead-phone" className="bg-charcoal border-white/15 h-12" />
                </div>
                <Input required type="email" value={form.email} onChange={set("email")} placeholder={t.lead.email} data-testid="lead-email" className="bg-charcoal border-white/15 h-12" />
                <Input required value={form.location} onChange={set("location")} placeholder={t.lead.location} data-testid="lead-location" className="bg-charcoal border-white/15 h-12" />

                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">{t.lead.usecase}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: "home", label: t.lead.home, icon: Home },
                      { id: "business", label: t.lead.business, icon: Building2 },
                    ].map((o) => {
                      const Icon = o.icon;
                      const active = form.use_case === o.id;
                      return (
                        <button
                          type="button"
                          key={o.id}
                          onClick={() => setForm({ ...form, use_case: o.id })}
                          data-testid={`lead-usecase-${o.id}`}
                          className={`flex items-center justify-center gap-2 h-12 rounded-lg border text-sm font-medium transition-colors duration-300 ${
                            active ? "border-emerald2 bg-emerald2/15 text-lime2" : "border-white/15 text-white/60 hover:border-white/30"
                          }`}
                        >
                          <Icon size={16} /> {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">{t.lead.gridType}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: "on_grid", label: t.lead.onGrid, icon: Plug },
                      { id: "off_grid", label: t.lead.offGrid, icon: PlugZap },
                    ].map((o) => {
                      const Icon = o.icon;
                      const active = form.grid_type === o.id;
                      return (
                        <button
                          type="button"
                          key={o.id}
                          onClick={() => setForm({ ...form, grid_type: o.id })}
                          data-testid={`lead-grid-${o.id}`}
                          className={`flex items-center justify-center gap-2 h-12 rounded-lg border text-sm font-medium transition-colors duration-300 ${
                            active ? "border-emerald2 bg-emerald2/15 text-lime2" : "border-white/15 text-white/60 hover:border-white/30"
                          }`}
                        >
                          <Icon size={16} /> {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">{t.lead.budget}</p>
                  <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                    <SelectTrigger data-testid="lead-budget" className="bg-charcoal border-white/15 h-12 text-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["unsure", "under_2k", "2k_5k", "5k_15k", "15k_plus"].map((k) => (
                        <SelectItem key={k} value={k} data-testid={`lead-budget-${k}`}>
                          {t.lead.budgetOptions[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const rec = recommended;
                    return (
                      <>
                      <p className="mt-2 text-xs text-white/50" data-testid="lead-recommended">
                        {t.lead.recommended}: <span className="text-lime2 font-medium">Gesung {rec.model}</span> · {t.catalog.from}{" "}
                        <span className="text-lime2 font-medium" data-testid="lead-recommended-price">
                          {cur.id !== "usd" ? `${t.catalog.approx} ` : ""}{formatQuote(quoteUsd, cur)}
                        </span>
                        {upfront && <span className="text-white/40"> · {t.catalog.upfrontNote.replace("{pct}", upfrontPct)}</span>}
                      </p>
                      {plan && (
                        <p className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-lime2/25 bg-lime2/5 px-2 py-1 text-[11px] text-lime2" data-testid="lead-finance">
                          {formatQuote(plan.monthly, cur)}{t.catalog.perMonth} × {plan.months} {t.catalog.monthsShort} · {t.catalog.financeDown} · +{Math.round(plan.rate * 100)}% {t.catalog.interestSuffix} · {t.catalog.totalPayable} {formatQuote(plan.total, cur)}
                        </p>
                      )}
                      </>
                    );
                  })()}
                </div>

                <Textarea value={form.message} onChange={set("message")} placeholder={t.lead.message} data-testid="lead-message" className="bg-charcoal border-white/15 min-h-[90px]" />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full py-4 transition-colors duration-300 disabled:opacity-60"
                >
                  <Send size={16} /> {loading ? t.lead.submitting : t.lead.submit}
                </button>
                <button
                  type="button"
                  onClick={openWhatsApp}
                  data-testid="lead-whatsapp"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-lime2/40 text-lime2 hover:bg-lime2/10 font-semibold py-3.5 transition-colors duration-300"
                >
                  <MessageCircle size={16} /> {t.lead.whatsapp}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
